/**
 * @file AddServiceModal.js
 * @description Modal for adding a new billed service (feature, cost type/frequency, dates, and amounts) to a client.
 */

import { useState, useEffect } from 'react';
import style from './modals.module.scss';
import Toast from '../toasts/Toast';
import CustomDatePicker from '../../helpers/CustomDatePicker_V2';
import BillFeatureDropdown from '../../shared/asyncDropdowns/BillFeatureDropdown';
import BillCostTypeDropdown from '../../shared/asyncDropdowns/BillCostTypeDropdown';
import BillCostFrequencyDropdown from '../../shared/asyncDropdowns/BillCostFrequencyDropdown';
import { createService } from '../../../services/clientService';
import { getText } from '../../../i18n';

const getInitialFormData = clientId => ({
    id: 0,
    tenantId: clientId,
    billFeatureId: null,
    billCostTypeId: null,
    billCostFrequencyId: null,
    minimumCost: '',
    effectiveDate: null,
    endDate: null,
    amount: '',
});

/**
 * AddServiceModal component for creating a new billed service for a client.
 * @param {Object} props - Component props.
 * @param {Function} props.onHide - Callback to close the modal.
 * @param {Function} [props.onSuccess] - Callback when the service is created successfully.
 * @param {Object} props.data - Modal data.
 * @param {number} props.data.clientId - The tenant ID of the client the service is added to.
 * @returns {React.ReactElement} The rendered modal component.
 */
function AddServiceModal({ onHide, onSuccess, data }) {
    const { clientId } = data || {};
    const [formData, setFormData] = useState(() =>
        getInitialFormData(clientId),
    );
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, []);

    const handleFieldChange = e => {
        const { name, value } = e.target;
        setFormData(prevData => ({
            ...prevData,
            [name]: value,
        }));
    };

    const handleAddService = async () => {
        if (
            !formData.billFeatureId ||
            !formData.billCostTypeId ||
            !formData.billCostFrequencyId
        ) {
            Toast.error(getText('msg_cc2bbe22c401'));
            return;
        }

        try {
            setIsSaving(true);
            await createService(
                {
                    ...formData,
                    minimumCost: Number(formData.minimumCost) || 0,
                    amount: Number(formData.amount) || 0,
                },
                clientId,
            );
            Toast.success(getText('msg_d67e57a13968'));
            if (onSuccess) {
                onSuccess();
            }
            onHide();
        } catch (error) {
            console.error('Failed to add service', error);
            const validationErrors = error.response?.data?.errors;

            if (validationErrors && typeof validationErrors === 'object') {
                Object.values(validationErrors)
                    .flat()
                    .forEach(message => Toast.error(message));
            } else {
                const errorMessage =
                    error.response?.data?.detail ||
                    error.response?.data?.title ||
                    getText('msg_b4e6cfad98ab');
                Toast.error(errorMessage);
            }
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className={`${style.container} modal-backdrop`}>
            <div
                className="modal fade show display-block"
                tabIndex="-1"
                aria-modal="true"
                role="dialog"
            >
                <div
                    className={`modal-dialog modal-dialog-centered ${style.modalSizing}`}
                >
                    <div className={`modal-content ${style.modalContent}`}>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h5 className={`m-0 ${style.title}`}>
                                {getText('msg_45e0f536dd5d')}
                            </h5>
                            <span
                                type="button"
                                onClick={onHide}
                                className="ing-text-buttons"
                            >
                                <i className="bi bi-x me-2" />
                                {getText('msg_bbfa773e5a63')}
                            </span>
                        </div>

                        <BillFeatureDropdown
                            selectedFeature={formData.billFeatureId}
                            onFeatureChange={value =>
                                setFormData(prevData => ({
                                    ...prevData,
                                    billFeatureId: value,
                                }))
                            }
                            inlineLabel={false}
                            isRequired
                        />

                        <BillCostTypeDropdown
                            selectedCostType={formData.billCostTypeId}
                            onCostTypeChange={value =>
                                setFormData(prevData => ({
                                    ...prevData,
                                    billCostTypeId: value,
                                }))
                            }
                            inlineLabel={false}
                            isRequired
                        />

                        <BillCostFrequencyDropdown
                            selectedCostFrequency={formData.billCostFrequencyId}
                            onCostFrequencyChange={value =>
                                setFormData(prevData => ({
                                    ...prevData,
                                    billCostFrequencyId: value,
                                }))
                            }
                            inlineLabel={false}
                            isRequired
                        />

                        <div className="form-group mb-3">
                            <label htmlFor="minimumCost">
                                {getText('msg_8b4360affdb0')}
                            </label>
                            <input
                                type="number"
                                id="minimumCost"
                                name="minimumCost"
                                className="form-control"
                                value={formData.minimumCost}
                                onChange={handleFieldChange}
                                min="0"
                                step="0.01"
                            />
                        </div>

                        <div className="row">
                            <div className="col-6">
                                <label htmlFor="effectiveDate">
                                    {getText('msg_120d93985118')}
                                </label>
                                <CustomDatePicker
                                    selectedDate={formData.effectiveDate}
                                    onDateChange={date =>
                                        setFormData(prevData => ({
                                            ...prevData,
                                            effectiveDate: date,
                                        }))
                                    }
                                />
                            </div>
                            <div className="col-6">
                                <label htmlFor="endDate">
                                    {getText('msg_84b147812589')}
                                </label>
                                <CustomDatePicker
                                    selectedDate={formData.endDate}
                                    onDateChange={date =>
                                        setFormData(prevData => ({
                                            ...prevData,
                                            endDate: date,
                                        }))
                                    }
                                />
                            </div>
                        </div>

                        <div className="form-group my-3">
                            <label htmlFor="amount">
                                {getText('msg_43dc8532f7e5')}
                            </label>
                            <input
                                type="number"
                                id="amount"
                                name="amount"
                                className="form-control"
                                value={formData.amount}
                                onChange={handleFieldChange}
                                min="0"
                                step="0.01"
                            />
                        </div>

                        <div className="d-flex justify-content-end">
                            <button
                                type="button"
                                className="btn ing-info-btn"
                                onClick={handleAddService}
                                disabled={isSaving}
                            >
                                {isSaving
                                    ? getText('msg_268c06a28a1b')
                                    : getText('msg_61cc55aa0453')}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default AddServiceModal;
